import store from 'redux/store';
import { getMerchantVersion, getSelectedProduct } from './helper';
import { CleverTapEventConfig, CleverTapEventConfigName } from './typing';

declare global {
  interface Window {
    clevertap: {
      event: {
        push: (event: string, payload: { [property: string]: any }) => void;
      };
    };
    L9RUM?: {
      addEvent: (event: string, payload: { [property: string]: any }) => void;
      identify: (payload: { id: string | number }) => void;
      init: (...args: any[]) => void;
      clearUser: () => void;
    };
  }
}

export const pushClevertapEvent = (eventConfig: CleverTapEventConfig): void => {
  const eventPayload = eventConfig?.eventPayload || {};
  if (
    window.clevertap?.event?.push &&
    typeof window.clevertap.event.push === 'function'
  )
    window.clevertap.event.push(eventConfig.eventName, {
      ...eventPayload,
      'Product Selected': getSelectedProduct(),
      'Merchant Version': getMerchantVersion() || '',
      'Dashboard Type': 'Unified Dashboard',
    });
  // Push event to Last9 RUM
  window?.L9RUM?.addEvent(eventConfig.eventName, {
    ...eventPayload,
    'Product Selected': getSelectedProduct(),
    'Merchant Version': getMerchantVersion() || '',
    'Dashboard Type': 'Unified Dashboard',
  });
};

export const pushProfileProperties = (data: Record<string, any>): void => {
  if ((window as any).clevertap) {
    (window as any).clevertap.profile.push({
      Site: {
        ...data,
      },
    });
  }
};

export const pushClevertapProfile = (
  identity: string | number | undefined
): void => {
  if ((window as any).clevertap && identity) {
    (window as any).clevertap.onUserLogin.push({
      Site: {
        Identity: identity,
      },
    });
  }
};

// Function to push profile data to Last9 RUM
export const pushLast9Profile = (
  data: Record<string, any>
): void => {
  if ((window as any).L9RUM?.identify && data?.id) {
    (window as any).L9RUM.identify({
      ...data
    });
  }
};

export const clevertapEventConfigList: {
  readonly [configName in CleverTapEventConfigName]: CleverTapEventConfig;
} = {
  SIGN_UP_SHOWN: {
    eventName: 'Signup Shown',
  },
  SIGN_UP_EXITED_SIGN_IN: {
    eventName: 'Signup Exited',
    eventPayload: {
      exit: 'Sign In',
    },
  },
  SIGN_UP_EXITED_CREATE_ACCOUNT: {
    eventName: 'Signup Exited',
    eventPayload: {
      exit: 'Create Account',
    },
  },
  LOGIN_SHOWN: {
    eventName: 'Login Shown',
  },
  LOGIN_PAGE_EXITED_SIGN_UP: {
    eventName: 'Login Page Exited',
    eventPayload: {
      exit: 'Sign Up',
    },
  },
  LOGIN_PAGE_EXITED_FORGOT_PASSWORD: {
    eventName: 'Login Page Exited',
    eventPayload: {
      exit: 'Forgot Password',
    },
  },
  LOGIN_PAGE_EXITED_CONTINUE: {
    eventName: 'Login Page Exited',
    eventPayload: {
      exit: 'Continue',
    },
  },
  RESET_PASSWORD_SHOWN: {
    eventName: 'Reset Password Shown',
  },
  RESET_PASSWORD_EXITED_CANCEL: {
    eventName: 'Reset Password Exited',
    eventPayload: {
      exit: 'Cancel',
    },
  },
  RESET_PASSWORD_EXITED_SEND_OTP: {
    eventName: 'Reset Password Exited',
    eventPayload: {
      exit: 'Send OTP',
    },
  },
  OVERVIEW_PAGE_SHOWN: {
    eventName: 'Overview Page Shown',
  },
  SIDEBAR_TAB_CLICKED_PAYMENT: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Payment',
    },
  },
  SIDEBAR_TAB_CLICKED_REFUNDS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Refunds',
    },
  },
  SIDEBAR_TAB_CLICKED_SETTLEMENTS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Settlements',
    },
  },
  SIDEBAR_TAB_CLICKED_DISPUTES: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Chargebacks',
    },
  },
  SIDEBAR_TAB_CLICKED_REPORTS:{
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Partner Reports'
    }
  },
  SIDEBAR_TAB_CLICKED_MERCHANT_REPORTS:{
    eventName: 'DB Tab Clicked',
    eventPayload: {
      tabClicked: 'Merchant Reports',
    },
  },
  SIDEBAR_TAB_CLICKED_ANALYTICS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Analytics',
    },
  },
  SIDEBAR_TAB_CLICKED_SETTINGS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Settings',
    },
  },
  SIDEBAR_TAB_CLICKED_PAYMENT_LINKS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Plural Links',
    },
  },
  SIDEBAR_TAB_CLICKED_PLANS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Plans',
    },
  },
  SIDEBAR_TAB_CLICKED_SUBSCRIPTIONS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Subscriptions',
    },
  },
  SIDEBAR_TAB_CLICKED_PAYMENT_PAGES: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Payment Pages',
    },
  },
  SIDEBAR_TAB_CLICKED_SMART_ROUTING: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Smart Routing',
    },
  },
  SIDEBAR_TAB_CLICKED_OFFERS: {
    eventName: 'Tab Clicked',
    eventPayload: {
      tabClicked: 'Offers',
    },
  },
  CONTACT_SUPPORT_SHOWN: {
    eventName: 'Contact Support Shown',
  },
  CONTACT_SUPPORT_EXITED_CLOSE: {
    eventName: 'Contact Support Exited',
  },
  CONTACT_SUPPORT_MAIL_ID_CLICKED: {
    eventName: 'Contact Support Email Clicked',
  },
  DOCUMENTATION_CLICKED: {
    eventName: 'Documentation Clicked',
  },
  USER_MANAGEMENT_CLICKED_PROFILE: {
    eventName: 'User Management Clicked',
    eventPayload: {
      source: 'Profile',
    },
  },
  USER_MANAGEMENT_CLICKED_SETTINGS: {
    eventName: 'User Management Clicked',
    eventPayload: {
      source: 'Settings',
    },
  },
  USER_MANAGEMENT_ROLE_CREATED: {
    eventName: 'Settings User Mgmt New Roles',
  },
  USER_MANAGEMENT_USER_CREATED: {
    eventName: 'Settings User Mgmt New User',
  },
  GENERAL_SETTINGS_URL_CHANGED: {
    eventName: 'Settings General URL Changed',
  },
  CHECKOUT_SETTINGS_THEME_CHANGED: {
    eventName: 'Settings Checkout Theme Changed',
  },
  SETTINGS_CREDENTIALS_SHOWN: {
    eventName: 'Settings Credentials Shown',
  },
  SETTINGS_CREDENTIALS_COPIED_MID: {
    eventName: 'Settings Credentials Copied',
    eventPayload: {
      copied: 'MID',
    },
  },
  SETTINGS_CREDENTIALS_COPIED_ACCESS_CODE: {
    eventName: 'Settings Credentials Copied',
    eventPayload: {
      copied: 'Access Code',
    },
  },
  SETTINGS_CREDENTIALS_COPIED_SECRET_KEY: {
    eventName: 'Settings Credentials Copied',
    eventPayload: {
      copied: 'Secret Key',
    },
  },
  SETTINGS_WEBHOOK_URL_CHANGED: {
    eventName: 'Settings Webhook URL Changes',
  },
  PAYMENT_PAGES_REDIRECTED: {
    eventName: 'Payment Pages Exited',
  },
  PAYMENT_LINKS_BULK_UPLOAD_HISTORY_VIEWED: {
    eventName: 'Payment Links Upload History Viewed',
  },
  PAYMENT_LINKS_REPORT_DOWNLOADED: {
    eventName: 'Payment Links Report Downloaded',
  },
  PAYMENT_LINK_CREATED_INDIVIDUAL: {
    eventName: 'Payment Links Created',
    eventPayload: {
      linkType: 'Individual',
    },
  },
  PAYMENT_LINK_CREATED_BULK: {
    eventName: 'Payment Links Created',
    eventPayload: {
      linkType: 'Bulk',
    },
  },
  SMART_ROUTING_GATEWAY_CREATED: {
    eventName: 'Smart Routing Gateway Created',
  },
  SMART_ROUTING_FAQ_SHOWN: {
    eventName: 'Smart Routing FAQ Shown',
  },
  SMART_ROUTING_PREFERENCE_SCORE_BIN_ROUTING_UPDATED: {
    eventName: 'Smart Routing Preferences BIN Routing Updated',
  },
  SMART_ROUTING_PREFERENCE_SCORE_ISSUER_ROUTING_UPDATED: {
    eventName: 'Smart Routing Issuer Routing Updated',
  },
  SMART_ROUTING_PREFERENCE_SCORE_CARD_BRAND_ROUTING_UPDATED: {
    eventName: 'Smart Routing Card Brand',
  },
  SMART_ROUTING_PRIORITY_LOGIC_UPDATED: {
    eventName: 'Smart Routing Priority Logic Updated',
  },
  SMART_ROUTING_CUSTOM_ROUTING_LOGIC_TURNED_ON: {
    eventName: 'Smart Routing Custom Logic Shown',
    eventPayload: {
      turnedOn: 'Yes',
    },
  },
  SMART_ROUTING_CUSTOM_ROUTING_LOGIC_TURNED_OFF: {
    eventName: 'Smart Routing Custom Logic Shown',
    eventPayload: {
      turnedOn: 'No',
    },
  },
  REFUND_BULK_UPLOAD_HISTORY_SHOWN: {
    eventName: 'Refunds Report Bulk History Shown',
  },
  REFUND_BULK_UPLOAD_SUCCESS: {
    eventName: 'Refunds Bulk Upload Done',
  },
  REFUND_REPORT_DOWNLOADED: {
    eventName: 'Refunds Report Downloaded',
  },
  PAYMENT_REPORT_DOWNLOADED: {
    eventName: 'Payments Report Downloaded',
  },
  OFFERS_REPORT_DOWNLOADED: {
    eventName: 'Offers Report Downloaded',
  },
  OVERVIEW_REPORT_DOWNLOADED: {
    eventName: 'Overview Report Downloaded',
  },
  LOGIN_OTP_PAGE_SHOWN: {
    eventName: 'Login OTP Page Shown',
  },
  LOGIN_OTP_SUBMITTED: {
    eventName: 'Login OTP Submitted',
  },
  LOGIN_OTP_ERROR_MESSAGE_SHOWN: {
    eventName: 'Login OTP Error Shown',
  },
  LOGIN_OTP_RESENT: {
    eventName: 'Login OTP Resent',
  },
  INCOMPATIBLE_DEVICE_DIALOG_SHOWN: {
    eventName: 'Incompatible Device Dialog Shown',
  },
  IMEI_BULK_UPLOAD_HISTORY_SHOWN: {
    eventName: 'IMEI Report Bulk History Shown',
  },
  PAN_ENTRY_SCREEN_VIEWED: {
  eventName: 'PAN Entry Screen Viewed',
  eventPayload: {
    description: 'Merchant lands on PAN entry screen',
  },
},
PAN_ENTERED: {
  eventName: 'PAN Entered',
  eventPayload: {
    description: 'Merchant enters PAN number',
  },
},
  BPCL_BANK_DETAILS_VIEWED: {
    eventName: 'BPCL Bank Details Viewed',
    eventPayload: {
      description: 'User viewed BPCL bank details page',
    },
  },
  BPCL_BANK_DETAILS_UPDATED: {
    eventName: 'BPCL Bank Details Updated',
    eventPayload: {
      description: 'User updated BPCL bank details',
    },
  },
  BPCL_BANK_DETAILS_APPROVED: {
    eventName: 'BPCL Bank Details Approved',
    eventPayload: {
      description: 'User approved BPCL bank details',
    },
  },
  BPCL_BANK_DETAILS_REJECTED: {
    eventName: 'BPCL Bank Details Rejected',
    eventPayload: {
      description: 'User rejected BPCL bank details',
    },
  },
  BPCL_BANK_DETAILS_SUBMITTED: {
    eventName: 'BPCL Bank Details Submitted',
    eventPayload: {
      description: 'User submitted BPCL bank details for review',
    },
  },
  MERCHANT_TAKE_ACTION_CLICKED: {
    eventName: 'Merchant Take Action Clicked',
  },
  BULK_UPLOAD_HISTORY_CLICKED: {
    eventName: 'Bulk Upload History Clicked',
  },
  BULK_ONBOARDING_CLICKED: {
    eventName: 'Bulk Onboarding Clicked',
  },
  DOWNLOAD_CLICKED: {
    eventName: 'Download Clicked',
  },
  WALLET_CREATE_SCREEN_VIEWED: {
    eventName: 'Wallet Create Screen Viewed',
  },
  WALLET_CREATE_CTA_CLICKED: {
    eventName: 'Wallet Create CTA Clicked',
  },
  WALLET_CREATION_SUBMITTED: {
    eventName: 'Wallet Creation Submitted',
  },
  WALLET_CREATION_SUCCESS: {
    eventName: 'Wallet Creation Success',
  },
  WALLET_CREATION_FAILED: {
    eventName: 'Wallet Creation Failed',
  },
  WALLET_CREATION_DOWNLOAD_ERROR: {
    eventName: 'Wallet Creation Download Error',
  },
  MONEY_LOAD_SCREEN_VIEWED: {
    eventName: 'Money Load Screen Viewed',
  },
  MONEY_LOAD_CTA_CLICKED: {
    eventName: 'Money Load CTA Clicked',
  },
  MONEY_LOAD_INITIATED: {
    eventName: 'Money Load Initiated',
  },
  MONEY_LOAD_SUCCESS: {
    eventName: 'Money Load Success',
  },
  MONEY_LOAD_FAILED: {
    eventName: 'Money Load Failed',
  },
  MONEY_LOAD_DOWNLOAD_ERROR: {
    eventName: 'Money Load Download Error',
  },
  WALLET_REFUND_INITIATED: {
    eventName: 'Wallet Refund Initiated',
  },
  WALLET_REFUND_REVIEW_SCREEN_VIEWED: {
    eventName: 'Wallet Refund Review Screen Viewed',
  },
  WALLET_REFUND_SUBMITTED: {
    eventName: 'Wallet Refund Submitted',
  },
  WALLET_REFUND_SUCCESS: {
    eventName: 'Wallet Refund Success',
  },
  WALLET_REFUND_FAILED: {
    eventName: 'Wallet Refund Failed',
  },
};


export const isCitiTheme = (): boolean => {
  if (window.location.href.includes("citi")) {
    return true;
  }

  if(store.getState().loginReducer.loginState.citi === true){
    return true;
  }

  if (sessionStorage.getItem('citi') === 'true') {
    return true;
  }

  return false;
};
